import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { compose, withHandlers, withState } from 'recompose';
import molecules from 'ctsutheme/components/molecules';
import atoms from 'ctsutheme/components/atoms';
import { FormViewContent } from 'components/FormViewContent';
import { withAppContext } from '@ctsu/loader';
import { setProtocolSync, getDocContent } from 'actions/protocols';
import { getProtocolDetails } from 'actions/protocols/protocoldetail';
import { bindLogger } from 'helpers/BindLogger';
import { openConfirmation } from 'helpers/Confirmation';
import { BoolToString, GetEmptyValue } from 'components';
import { httpStatusEnum } from 'appconstants';
import { GetFromCtxValue } from 'helpers';
import PropTypes from 'prop-types';
import { FarWindowRestore, FasSyncAlt, FasHistory, FasLock } from 'assets/icons';
import { CanUserViewSync } from 'appconstants/Permission';
import { RenderElement } from 'components/RenderHelpers';
import AccrualInfo from './AccrualInfo';
import InternalComments from './InternalComments';

const {
  Divider,
  Typography,
  IconButton,
  Tooltip
} = atoms;
const { List, ListSubHeader } = molecules;

function getDocStatus(sts) {
  return ['In Review', 'Approval on Hold', 'Withdrawn', 'Disapproved'].includes(sts);
}

function getDocTitle(sts, docId) {
  if (getDocStatus(sts)) {
    return 'Protocol has not been Approved';
  }
  if (docId === null) {
    return 'Protocol Document not available';
  }
  return 'Protocol Document';
}

const PrtocolSummary = ({
  prtInfo,
  state,
  setState,
  handleHistClick,
  setPrtSync,
  downloadDocument
}) => {

  const {
    protocolNumber,
    documentId,
    accrualInfo,
    grpProtStatus,
    grpProtStatusDate,
    title,
    leadGrp,
    phase,
    currentVersion,
    currentVersionDate,
    nihAdminCode,
    indIdeType,
    nctNumber,
    // isCirbRequired,
    isAyaStudy,
    isRssStatusSync,
    isTreatment,
    isPIO,
    pioStatus,
    pioStatusDate,
    activatedDate,
    localDocumentNumber,
    statusLockedInPIO,
    ctsuDiseaseNames,
    comments,
    /*
      RSSMOD-905 : https://jira.westat.com/browse/RSSMOD-905
    */
    isStatusTransactionPending
  } = prtInfo;
  
  /*
    RSSMOD-905 : https://jira.westat.com/browse/RSSMOD-905
    On checking isStatusTransactionPending directly is not reloading the component as initially the field is
    in undefined state, so using a state variable called statusTransactionFlag to check the isStatusTransactionPending value.
  */
  const [statusTransactionFlag, setStatusTransactionFlag] = useState(false);
  useEffect(() => {
    if(isStatusTransactionPending && isStatusTransactionPending === true) {
      setStatusTransactionFlag(true);
    }
  }, [isStatusTransactionPending, setStatusTransactionFlag]);

  
  useEffect(() => {
    setState(false);
  }, [prtInfo, setState]);

  return (
    <>
      <List
        subheader={(
          <ListSubHeader flex>
            <Typography data-id="prtNumTitle" primary header style={{ textAlign: 'right' }}>
              Protocol Number
            </Typography>
            <div className="protocol-details-subheader-wrapper">
              <Typography data-id="prtNum" inline primary className="protocol-details-number" style={{ marginLeft: '.5rem' }}>
                <GetEmptyValue value={protocolNumber} />
              </Typography>
              <Tooltip
                tooltipplacementbottom="true"
                title={getDocTitle(grpProtStatus, documentId)}
                aria-label="Protocol Document"
              >
                <IconButton
                  data-id="protocol-details-documents"
                  aria-label="protocol-details-documents"
                  color="inherit"
                  className="mx-1"
                  disabled={(documentId === null || getDocStatus(grpProtStatus))}
                  onClick={() => downloadDocument(documentId)}
                >
                  <FarWindowRestore />
                </IconButton>
              </Tooltip>
              <Tooltip
                tooltipplacementbottom="true"
                title="Protocol Fact Sheet"
                aria-label="Protocol Fact Sheet"
              >
                <IconButton
                  data-id="protocol-fact-sheet"
                  aria-label="Protocol Fact Sheet"
                  color="inherit"
                  className="mx-1"
                  disabled
                  onClick={() => true}
                >
                  <FarWindowRestore />
                </IconButton>
              </Tooltip>
              {/*
                RSSMOD-905 : https://jira.westat.com/browse/RSSMOD-905
              */}
              <RenderElement access={statusTransactionFlag}>
                <Tooltip
                  tooltipplacementbottom="true"
                  title="Lock Protocol"
                  aria-label="Lock Protocol"
                >
                  <IconButton
                    data-id="lock-protocol"
                    aria-label="Lock Protocol"
                    color="secondary"
                    disabled
                    className="mx-1 color-grey-disabled"
                  >
                    <FasLock />
                  </IconButton>
                </Tooltip>
              </RenderElement>
              <RenderElement access={CanUserViewSync()}>
                <Tooltip
                  tooltipplacementbottom="true"
                  title="Sync Protocol"
                  aria-label="Sync Protocol"
                >
                  {/*
                    RSSMOD-905 : https://jira.westat.com/browse/RSSMOD-905
                    Disabled if status transaction lock icon is displayed
                  */}
                  <IconButton
                    data-id="sync-protocol"
                    aria-label="Sync Protocol"
                    color="secondary"
                    className={`mx-1 ${isStatusTransactionPending === true ? 'color-grey-disabled' : ''}`}
                    disabled={ isStatusTransactionPending === true }
                    onClick={() => setPrtSync()}
                  >
                    <FasSyncAlt />
                  </IconButton>
                </Tooltip>
              </RenderElement>
            </div>
          </ListSubHeader>
        )}
      >
        <Divider />
        <FormViewContent
          more
          primaryText="Protocol Title"
          name="prtTitle"
          secondaryText={<GetEmptyValue value={title} />}
        />
        <Divider />
        <FormViewContent
          primaryText="Lead Group"
          name="leadGrp"
          secondaryText={<GetEmptyValue value={leadGrp} />}
        />
        <Divider />
        <FormViewContent
          primaryText="Phase"
          name="phase"
          secondaryText={<GetEmptyValue value={phase} />}
        />
        <Divider />
        <FormViewContent
          primaryText="Disease"
          name="ctsuDiseaseNames"
          secondaryText={<GetEmptyValue value={ctsuDiseaseNames} />}
        />
        <Divider />
        <FormViewContent
          primaryText="Group Protocol Status"
          name="grpPritStatus"
          secondaryText={(
            <>
              <GetEmptyValue value={grpProtStatus} />
&nbsp;(
              <GetEmptyValue value={grpProtStatusDate} />
&nbsp;ET)&nbsp;&nbsp;
              <Tooltip
                tooltipplacementbottom="true"
                title="View Status History"
                aria-label="View Status History"
                style={{ display: 'inline-flex' }}
              >
                <Typography link icon>
                  <IconButton
                    aria-label="View Status History"
                    data-id="viewStatus"
                    color="inherit"
                    onClick={handleHistClick}
                  >
                    <FasHistory />
                  </IconButton>
                </Typography>
              </Tooltip>
            </>
          )}
        />
        <Divider />
        <FormViewContent
          primaryText="Current Version"
          name="currentVersion"
          secondaryText={
            currentVersion
            && currentVersionDate && (
              <>
                <GetEmptyValue value={currentVersion} />
&nbsp;(
                <GetEmptyValue value={currentVersionDate} />
)
              </>
            )
          }
        />
        <Divider />

        <FormViewContent
          primaryText="NIH Admin Code"
          name="nihAdminCode"
          secondaryText={<GetEmptyValue value={nihAdminCode} />}
        />
        <Divider />
        <FormViewContent
          primaryText="IND Type"
          name="indIdeType"
          secondaryText={<GetEmptyValue value={indIdeType} />}
        />
        <Divider />

        <FormViewContent
          primaryText="NCT Number"
          name="nctNumber"
          secondaryText={<GetEmptyValue value={nctNumber} />}
        />
        <Divider />

        {/* <FormViewContent
          primaryText="CIRB Required?"
          secondaryText={<BoolToString boolValue={isCirbRequired} />}
        />
        <Divider /> */}

        <FormViewContent
          primaryText="AYA Trial?"
          name="isAyaStudy"
          secondaryText={<BoolToString boolValue={isAyaStudy} />}
        />
        <Divider />

        <FormViewContent
          primaryText="RSS Status Sync"
          name="isRssStatusSync"
          secondaryText={<BoolToString boolValue={isRssStatusSync} />}
        />
        <Divider />

        <FormViewContent
          primaryText="Treatment Flag"
          name="isTreatment"
          secondaryText={<BoolToString boolValue={isTreatment} />}
        />
        <Divider />
        <FormViewContent
          primaryText="In PIO?"
          name="isPIO"
          secondaryText={<BoolToString boolValue={isPIO} />}
        />
        <Divider />
        <FormViewContent
          primaryText="PIO Status"
          name="pioStatus"
          secondaryText={
            prtInfo.pioStatus ? (
              <>
                <GetEmptyValue value={pioStatus} />
&nbsp;(
                <GetEmptyValue value={pioStatusDate} />
)
              </>
            ) : (
              ''
            )
          }
        />
        <Divider />
        <FormViewContent
          primaryText="Activated on"
          name="activatedDate"
          secondaryText={<GetEmptyValue value={activatedDate} />}
        />
        <Divider />
        <FormViewContent
          primaryText="Local Study #"
          name="localDocumentNumber"
          secondaryText={<GetEmptyValue value={localDocumentNumber} />}
        />
        <Divider />
        <FormViewContent
          primaryText="Status locked in PIO?"
          name="statusLockedInPIO"
          secondaryText={<BoolToString boolValue={statusLockedInPIO === 'Y'} />}
        />
        <Divider />
        <AccrualInfo accrualInfo={accrualInfo} />
        <Divider />
        <RenderElement access>
          <InternalComments
            comments={comments}
          />
        </RenderElement>
      </List>
    </>
  );
};

PrtocolSummary.propTypes = {
  prtInfo: PropTypes.oneOfType([PropTypes.object]),
  state: PropTypes.bool,
  setState: PropTypes.func,
  handleHistClick: PropTypes.func,
  setPrtSync: PropTypes.func,
  downloadDocument: PropTypes.func
};

PrtocolSummary.defaultProps = {
  prtInfo: null,
  state: false,
  setState: null,
  handleHistClick: null,
  setPrtSync: null,
  downloadDocument: null
};

const withConnectState = connect(
  (state, ownProps) => ({}),
  dispatch => bindActionCreators(
    {
      onSetProtocolSync: setProtocolSync,
      onGetDocument: getDocContent,
      onGetSummary: getProtocolDetails
    },
    dispatch
  )
);

const ProtocolInfo = compose(
  withConnectState,
  withAppContext,
  withState('state', 'setState', false),
  withHandlers({
    toggleContact: props => event => {
      const { setState, state } = props;
      setState(!state);
    }
  }),
  withHandlers({
    handleHistClick: props => event => {
      const { onStatusHistory, prtInfo, onOpenModal } = props;
      onStatusHistory(event, prtInfo.prtId);
      onOpenModal({
        type: 'StatusChangeHistory',
        title: 'Protocol Status History',
        data: null
      });
    },
    setPrtSync: props => event => {
      const {
        context, prtInfo, onSetProtocolSync
      } = props;
      const { protocolNumber } = prtInfo;
      
      const refreshPage = () => {
        window.location.reload();
      }
      bindLogger(onSetProtocolSync, context)(protocolNumber).subscribe(
        resp => {
          if (resp.status === httpStatusEnum.SUCCESS) {
            openConfirmation({
              message: GetFromCtxValue(context, 'PROTOCOL_SYNC_SUCCEESS'),
              onAction: refreshPage,
              name: 'summary'
            });
          }
        }
      );
    },
    downloadDocument: props => docId => {
      const { context, onGetDocument } = props;
      if (docId !== null) {
        bindLogger(onGetDocument, context)(docId);
      }
    }
  })
)(PrtocolSummary);

export default ProtocolInfo;
